import { SideBarCard, toast } from '@aus-platform/design-system';
import { isEmpty, isNil } from 'lodash';
import { Map3DSideBarContext } from 'map-3d/contexts';
import { useContext, useEffect, useRef, useState } from 'react';
import { AbortError } from 'shared/errors';
import { useAppSelector } from 'src/app/hooks';
import {
  selectMap3dSidebar,
  selectMap3DState,
  selectMap3dWorkspace,
} from 'src/map-3d/shared/map-3d-slices';
import { FileType } from 'src/shared/api';
import { useCRSInfo } from 'src/shared/api/crs';
import { EPSGCode } from 'src/shared/enums';
import {
  isCRSDefined,
  registerCRSFromInfo,
} from 'src/shared/helpers/crs-helpers';
import { GraphViewer } from './components/graph-viewer';
import type { CrossSectionPointData } from './components/graph-viewer/types';
import { initialCrossSectionConfig } from './constants';
import { CrossSectionSidecard } from './cross-section-sidecard';
import {
  type CrossSectionConfig,
  type CrossSectionLineData,
  type HighlightConfig,
} from './types';
import { CopcReaderInstance } from './utils/copc-reader';
import { CrossSectionExtractor } from './utils/cross-section-extractor';
import {
  parseCrsFromWkt,
  reprojectCoordinates,
  transformCrossSectionPoints,
} from './utils/helpers';

export const CrossSectionTool: React.FC = () => {
  // States.
  const [isGraphVisible, setIsGraphVisible] = useState(false);
  const [isGraphExpanded, setIsGraphExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [graphData, setGraphData] = useState<CrossSectionPointData | null>(
    null,
  );
  const [isError, setIsError] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [pathStartEnd, setPathStartEnd] = useState<{
    start: { lat: number; lng: number };
    end: { lat: number; lng: number };
  } | null>(null);
  const [highlightConfig, setHighlightConfig] = useState<HighlightConfig>({
    line: null,
    sourceCrs: null,
  });
  const [crossSectionConfig, setCrossSectionConfig] =
    useState<CrossSectionConfig>(initialCrossSectionConfig);

  // Refs.
  const abortControllerRef = useRef<AbortController | null>(null);
  const readerRef = useRef<CopcReaderInstance | null>(null);

  // Selectors.
  const { activeSidebarOption } = useAppSelector(selectMap3dSidebar);
  const { workspaceLayers, selectedPointCloudLayerId } =
    useAppSelector(selectMap3dWorkspace);

  const { cesiumProxy } = useAppSelector(selectMap3DState);

  // Contexts.
  const { hideSidecard } = useContext(Map3DSideBarContext);

  // Hooks.
  const { data: crsInfo, isError: isCrsError } = useCRSInfo(
    String(crossSectionConfig.targetCrs),
    crossSectionConfig.needsCRSFetch &&
      crossSectionConfig.targetCrs !== EPSGCode.WGS84,
  );

  // Initialize reader ref.
  readerRef.current = crossSectionConfig.copcReader;

  const cleanupCrossSectionResources = (reader?: CopcReaderInstance | null) => {
    if (!isNil(reader)) {
      reader.close();
    }
    abortControllerRef.current?.abort();
    setCrossSectionConfig(initialCrossSectionConfig);
  };

  const initializeCrossSection = async (lineData: CrossSectionLineData) => {
    abortControllerRef.current?.abort();

    const previousReader = crossSectionConfig.copcReader;

    if (!isNil(previousReader)) {
      previousReader.close();
    }

    setCrossSectionConfig(initialCrossSectionConfig);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsGenerating(true);
    setIsLoading(true);
    setIsGraphVisible(true);
    setExtractionProgress(0);
    setGraphData(null);
    setIsError(false);

    let reader: CopcReaderInstance | null = null;

    try {
      if (isNil(selectedPointCloudLayerId)) {
        throw new Error('No point cloud layer selected');
      }

      const selectedLayer = workspaceLayers[selectedPointCloudLayerId];

      if (isNil(selectedLayer)) {
        throw new Error('Selected point cloud layer not found');
      }

      const copcFile = selectedLayer.files?.find(
        (file) => file.type === FileType.Copc,
      );

      if (isNil(copcFile) || isNil(copcFile.downloadUrl)) {
        throw new Error(
          'COPC file not found for selected layer. ' +
            'Please ensure the point cloud layer has a COPC file uploaded.',
        );
      }

      const copcUrl = copcFile.downloadUrl;
      const pathWGS84 = lineData.pathWGS84;

      if (isNil(pathWGS84) || pathWGS84.length < 2) {
        throw new Error(
          'Invalid line path. Path must contain at least 2 points.',
        );
      }

      const buffer = lineData.buffer;

      if (isNaN(buffer) || buffer <= 0) {
        throw new Error('Sample distance must be a positive number');
      }

      const layerCrs = selectedLayer.sourceCrs;

      if (isNil(layerCrs)) {
        throw new Error(
          'Point cloud layer does not have a source CRS. ' +
            'Please ensure the layer has a valid CRS configured.',
        );
      }

      const startCoord = pathWGS84[0];
      const endCoord = pathWGS84[pathWGS84.length - 1];

      setPathStartEnd({
        start: { lat: startCoord[1], lng: startCoord[0] },
        end: { lat: endCoord[1], lng: endCoord[0] },
      });

      reader = new CopcReaderInstance(copcUrl);
      await reader.initialize(abortControllerRef.current?.signal);

      const copcWkt = reader.getWkt();
      const copcCrs = parseCrsFromWkt(copcWkt);

      const targetCrs = copcCrs ?? layerCrs;
      const needsCRSFetch = !isCRSDefined(targetCrs);

      setHighlightConfig({
        line: lineData.line,
        sourceCrs: targetCrs,
      });

      const newConfig: CrossSectionConfig = {
        copcCrs,
        layerCrs,
        targetCrs,
        needsCRSFetch,
        copcReader: reader,
        initializationComplete: true,
        lineData,
        copcUrl,
        pathWGS84,
        buffer,
      };

      setCrossSectionConfig(newConfig);

      if (!needsCRSFetch) {
        generateCrossSection(newConfig);
      }
    } catch (error) {
      if (!(error instanceof AbortError)) {
        toast.error('Failed to generate cross section. Please try again.');
        setIsError(true);
        setIsGraphVisible(true);
        setIsLoading(false);
        setIsGenerating(false);
      }
      cleanupCrossSectionResources(reader);
    }
  };

  const generateCrossSection = async (
    config: CrossSectionConfig = crossSectionConfig,
  ) => {
    const { copcReader, copcUrl, targetCrs, pathWGS84, buffer } = config;

    try {
      const hasValidConfig =
        !isNil(copcReader) &&
        !isNil(copcUrl) &&
        !isNil(targetCrs) &&
        !isNil(pathWGS84) &&
        !isNil(buffer);

      if (!hasValidConfig) {
        throw new Error('Invalid cross section configuration');
      }

      const extractor = new CrossSectionExtractor(copcUrl);

      const reprojectedPath = reprojectCoordinates(
        pathWGS84,
        EPSGCode.WGS84,
        targetCrs,
      );

      const points = await extractor.extractCrossSection(
        copcReader,
        reprojectedPath,
        buffer,
        targetCrs,
        {
          signal: abortControllerRef.current?.signal,
          onProgress: (progress) => {
            setExtractionProgress(progress);
          },
        },
      );

      const viewerData = transformCrossSectionPoints(points);

      if (isEmpty(points)) {
        throw new Error('No points found');
      }

      toast.success('Cross Section generated successfully.');
      setGraphData(viewerData);
      setIsGraphVisible(true);
      setIsLoading(false);
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      }

      toast.error('Failed to generate cross section. Please try again.');
      setIsError(true);
      setIsGraphVisible(true);
      setIsLoading(false);
      setIsGenerating(false);
    } finally {
      setIsGenerating(false);
      setExtractionProgress(0);
      cleanupCrossSectionResources(copcReader);
    }
  };

  const handleOpenGraphViewer = () => {
    setIsGraphVisible(true);
  };

  useEffect(() => {
    if (cesiumProxy) {
      cesiumProxy.viewer.scene.requestRenderMode = true;
    }
    return () => {
      if (cesiumProxy) {
        cesiumProxy.viewer.scene.requestRenderMode = false;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      if (readerRef.current) {
        readerRef.current.close();
        readerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    try {
      if (isCrsError) {
        throw new Error('CRS fetch failed');
      }

      if (isNil(crsInfo)) {
        return;
      }
      registerCRSFromInfo(crsInfo);
      generateCrossSection();
    } catch {
      toast.error('Failed to generate cross section. Please try again.');
      setIsError(true);
      setIsGraphVisible(true);
      setIsLoading(false);
      setIsGenerating(false);
      cleanupCrossSectionResources(crossSectionConfig.copcReader);
    }
  }, [crsInfo, isCrsError]);

  return (
    <div>
      <SideBarCard
        className="aus-sidebar-item__sidecard map-3d-sidebar-card"
        title="Cross Section"
        show={activeSidebarOption?.isHidden}
        onClose={hideSidecard}
      >
        <CrossSectionSidecard
          onGenerateCrossSection={initializeCrossSection}
          onOpenGraphViewer={handleOpenGraphViewer}
          isGenerating={isGenerating || isLoading}
          isGraphVisible={isGraphVisible}
        />
      </SideBarCard>

      <GraphViewer
        data={graphData}
        isLoading={isLoading}
        isGraphError={isError}
        isVisible={isGraphVisible}
        isExpanded={isGraphExpanded}
        onClose={() => setIsGraphVisible(false)}
        onToggleExpand={() => setIsGraphExpanded(!isGraphExpanded)}
        pathStartEnd={pathStartEnd ?? undefined}
        progressPercentage={extractionProgress}
        highlightConfig={highlightConfig}
      />
    </div>
  );
};
